import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/api/supabase';
import type { User, Session, AuthError } from '@supabase/supabase-js';

// Session timeout in milliseconds (30 minutes of inactivity)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: AuthError | null;
  needsPasswordReset: boolean;
}

export interface UseAuthReturn extends AuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, metadata?: Record<string, unknown>) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  clearPasswordResetFlag: () => void;
}

/**
 * Authentication hook providing auth state and methods
 * Automatically syncs with Supabase auth state changes
 *
 * @example
 * ```tsx
 * const { user, loading, signIn, signOut } = useAuth();
 *
 * if (loading) return <Spinner />;
 * if (!user) return <LoginForm onSubmit={signIn} />;
 * return <div>Welcome {user.email}</div>;
 * ```
 */
export function useAuthInternal(): UseAuthReturn {
  const [state, setState] = useState<AuthState>(() => ({
    user: null,
    session: null,
    loading: true,
    error: null,
    needsPasswordReset:
      typeof localStorage !== 'undefined' &&
      localStorage.getItem('needsPasswordReset') === 'true',
  }));

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const userRef = useRef(state.user);
  useEffect(() => { userRef.current = state.user; }, [state.user]);

  // Session timeout handler - logs out user after inactivity
  const resetSessionTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only set timeout if user is logged in
    if (userRef.current) {
      timeoutRef.current = setTimeout(async () => {
        console.log('Session timeout - logging out due to inactivity');
        await supabase.auth.signOut();
        localStorage.removeItem('needsPasswordReset');
        setState({
          user: null,
          session: null,
          loading: false,
          error: null,
          needsPasswordReset: false,
        });
      }, SESSION_TIMEOUT_MS);
    }
  }, []);

  // Set up activity listeners for session timeout
  useEffect(() => {
    if (!state.user) return;

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];

    const handleActivity = (): void => {
      resetSessionTimeout();
    };

    // Add listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timeout
    resetSessionTimeout();

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [state.user, resetSessionTimeout]);

  // Initialize auth state - register listener FIRST to avoid missing events
  useEffect(() => {
    let mounted = true;

    // Listen for auth changes before any async work to avoid missing PASSWORD_RECOVERY
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      const isPasswordRecovery = event === 'PASSWORD_RECOVERY';
      if (isPasswordRecovery) {
        localStorage.setItem('needsPasswordReset', 'true');
      }
      setState((prev) => ({
        ...prev,
        user: session?.user ?? null,
        session,
        loading: false,
        error: null,
        needsPasswordReset: isPasswordRecovery || localStorage.getItem('needsPasswordReset') === 'true',
      }));
    });

    // Then do the initial session check
    async function initializeAuth(): Promise<void> {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        if (mounted) {
          setState((prev) => ({
            ...prev,
            user: session?.user ?? null,
            session,
            loading: false,
            error: null,
          }));
        }
      } catch (error) {
        if (mounted) {
          localStorage.removeItem('needsPasswordReset');
          setState((prev) => ({
            ...prev,
            user: null,
            session: null,
            loading: false,
            error: error as AuthError,
            needsPasswordReset: false,
          }));
        }
      }
    }

    void initializeAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Sign in with email and password
   */
  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw error;
      }

      setState((prev) => ({
        ...prev,
        user: data.user,
        session: data.session,
        loading: false,
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as AuthError,
      }));
      throw error;
    }
  }, []);

  /**
   * Sign up with email and password
   */
  const signUp = useCallback(
    async (
      email: string,
      password: string,
      metadata?: Record<string, unknown>
    ): Promise<void> => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const signUpOptions = metadata ? { data: metadata } : {};
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          ...(Object.keys(signUpOptions).length > 0 ? { options: signUpOptions } : {}),
        });

        if (error) {
          throw error;
        }

        // Profile is automatically created by the database trigger
        // using metadata passed in the signup options

        setState((prev) => ({
          ...prev,
          user: data.user,
          session: data.session,
          loading: false,
          error: null,
        }));
      } catch (error) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: error as AuthError,
        }));
        throw error;
      }
    },
    []
  );

  /**
   * Sign out current user
   */
  const signOut = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const { error } = await supabase.auth.signOut();

      if (error) {
        throw error;
      }

      localStorage.removeItem('needsPasswordReset');
      setState({
        user: null,
        session: null,
        loading: false,
        error: null,
        needsPasswordReset: false,
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as AuthError,
      }));
      throw error;
    }
  }, []);

  /**
   * Clear the password reset flag after user has successfully changed their password
   */
  const clearPasswordResetFlag = useCallback(() => {
    localStorage.removeItem('needsPasswordReset');
    setState((prev) => ({ ...prev, needsPasswordReset: false }));
  }, []);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(async (email: string): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        throw error;
      }

      setState((prev) => ({ ...prev, loading: false, error: null }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error as AuthError,
      }));
      throw error;
    }
  }, []);

  return {
    ...state,
    signIn,
    signUp,
    signOut,
    resetPassword,
    clearPasswordResetFlag,
  };
}
